import { useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";

const schema = z.object({
  email: z.string().email("Email inválido"),
});

type Form = z.infer<typeof schema>;

export default function RequestPasswordResetPage() {
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Form>({ resolver: zodResolver(schema) });

  async function onSubmit(values: Form) {
    setServerError(null);
    try {
      await api.post("/auth/request-password-reset", {
        email: values.email.toLowerCase(),
      });
      setSubmitted(true);
    } catch {
      setServerError("No se pudo procesar la solicitud. Intenta más tarde.");
    }
  }

  return (
    <div className="min-h-[100dvh] bg-white flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-sm animate-fade-up">
        <div className="mb-14">
          <img
            src="https://raw.githubusercontent.com/Jefferspri/ponos-host-images/main/logo.png"
            alt="NiuFit Partners"
            className="h-8 w-auto"
            style={{ filter: "invert(1) brightness(0.05)" }}
          />
        </div>

        {submitted ? (
          <div>
            <div className="mb-8 flex items-center gap-3">
              <div className="rounded-full bg-emerald-50 p-2 border border-emerald-100">
                <CheckCircle2 className="h-5 w-5 text-emerald-700" strokeWidth={2} />
              </div>
              <h2 className="font-display text-4xl font-medium tracking-tight leading-none">
                Revisa tu<br />correo.
              </h2>
            </div>
            <p className="text-sm text-muted-foreground mb-8 leading-relaxed">
              Si tu email está registrado, recibirás un enlace para establecer una nueva contraseña.
              El enlace es válido por unas horas.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 text-sm text-ink font-medium hover:underline underline-offset-4 decoration-2 decoration-neutral-400"
            >
              Volver a Ingresar
              <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.25} />
            </Link>
          </div>
        ) : (
          <>
            <div className="mb-10">
              <h2 className="font-display text-4xl font-medium tracking-tight leading-none">
                Recupera<br />tu acceso.
              </h2>
              <p className="text-sm text-muted-foreground mt-4 leading-relaxed">
                Ingresa el email de tu cuenta. Te enviaremos un enlace para establecer una nueva contraseña.
              </p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="space-y-1.5">
                <label className="block text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-medium">
                  Email
                </label>
                <input
                  type="email"
                  autoComplete="email"
                  placeholder="tu@partner.com"
                  className="w-full bg-transparent border-b border-border pb-2.5 pt-2 text-[15px] outline-none focus:border-ink transition-colors placeholder:text-muted-foreground/50"
                  {...register("email")}
                />
                {errors.email && (
                  <p className="text-xs text-destructive pt-1">{errors.email.message}</p>
                )}
              </div>

              {serverError && (
                <div className="rounded border border-destructive/25 bg-destructive/[0.03] px-3 py-2 text-sm text-destructive">
                  {serverError}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="group relative w-full h-14 rounded-md bg-ink text-white font-semibold overflow-hidden transition-all duration-300 shadow-[0_1px_2px_rgba(0,0,0,0.08)] hover:shadow-[0_16px_32px_-12px_rgba(0,0,0,0.35),_0_0_0_1px_rgba(193,199,206,0.35)] active:translate-y-[1px] disabled:opacity-80 disabled:cursor-wait"
              >
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 -skew-x-12 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 transition-all duration-700 group-hover:opacity-100 group-hover:translate-x-[380%]"
                />
                <span className="relative flex items-center justify-between px-6">
                  <span className="text-[15px] tracking-tight inline-flex items-baseline gap-0.5">
                    {isSubmitting ? "Enviando" : "Enviar enlace"}
                    {isSubmitting && (
                      <span aria-hidden className="inline-flex gap-0.5 ml-0.5">
                        <span className="h-1 w-1 rounded-full bg-silver-hi/70 animate-pulse-dot" style={{ animationDelay: "0ms" }} />
                        <span className="h-1 w-1 rounded-full bg-silver-hi/70 animate-pulse-dot" style={{ animationDelay: "200ms" }} />
                        <span className="h-1 w-1 rounded-full bg-silver-hi/70 animate-pulse-dot" style={{ animationDelay: "400ms" }} />
                      </span>
                    )}
                  </span>
                  <ArrowUpRight
                    className={[
                      "h-4 w-4 text-silver-hi transition-all duration-300",
                      isSubmitting ? "opacity-40" : "group-hover:translate-x-1 group-hover:-translate-y-1",
                    ].join(" ")}
                    strokeWidth={2.25}
                  />
                </span>
                {isSubmitting && (
                  <span
                    aria-hidden
                    className="pointer-events-none absolute bottom-0 left-0 right-0 h-[2px] overflow-hidden"
                  >
                    <span className="absolute inset-y-0 w-1/4 bg-gradient-to-r from-transparent via-silver-hi to-transparent animate-slide-progress" />
                  </span>
                )}
              </button>
            </form>

            <div className="mt-14 pt-6 border-t border-border/60 text-xs text-muted-foreground flex items-center justify-between">
              <span>¿La recuerdas?</span>
              <Link
                to="/login"
                className="text-ink font-medium hover:underline underline-offset-4 decoration-2 decoration-neutral-400"
              >
                Volver a Ingresar →
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
